import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import LegacyCollectionsAppLayout from '../components/LegacyCollectionsAppLayout';
import ItemContext from '../components/contexts/ItemContext';
import ItemsList from '../components/ItemsList';
import { routes } from '../constants';
import LegacyBreadcrumbs from '../components/LegacyBreadcrumbs';
import { selectPinnedContents, selectPinnedPagination, selectPinnedStatus } from '../reducers/pinned.selectors';
import { createNextPaginationRequest, createPaginationRequest, hasNext } from '../utils/pagination';
import { fetchPinnedContents as fetchPinnedContentsAction } from '../actions/pinned.actions';
import { useNewNavigation } from '../utils/useNewNavigation';
import CollectionsAppLayout from '../components/AppLayout/Collections/CollectionsAppLayout';
import CollectionsTabs from '../components/AppLayout/Collections/CollectionsTabs';
import MasterLayout from '../components/AppLayout/MasterLayout';
import { selectUser } from '../reducers/user.selectors';
import useAction from '../utils/useAction';

const PinnedItemListPage: React.FC = () => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const pinnedPagination = useSelector(selectPinnedPagination);
  const pinnedContents = useSelector(selectPinnedContents);
  const pinnedStatus = useSelector(selectPinnedStatus);
  const fetchPinnedContents = useAction(fetchPinnedContentsAction);
  const showNewNavigation = useNewNavigation();

  const paginationRequest = createPaginationRequest(pinnedPagination);
  const nextPaginationRequest = createNextPaginationRequest(pinnedPagination);

  useEffect(() => {
    if (user && paginationRequest.offset && paginationRequest.offset > 0) {
      fetchPinnedContents(paginationRequest);
    }
  }, [user]);

  const [activeId, setActiveId] = useState('');
  const activeItem = pinnedContents.find((item) => item.id === activeId);

  const mainBody = <ItemsList
    isLoading={pinnedStatus === 'loading'}
    hasNext={hasNext(pinnedPagination)}
    total={pinnedStatus === 'loading' ? -1 : pinnedContents.length}
    loadNext={() => {
      return fetchPinnedContents(nextPaginationRequest);
    }}
    activeId={activeId}
    items={pinnedContents}
    linkRoot={[routes.pinned.root]}
    setActiveId={setActiveId}
  />;

  const context = pinnedContents.length ? <ItemContext item={activeItem} /> : <></>;

  const oldLayout = <LegacyCollectionsAppLayout
    header={
      <LegacyBreadcrumbs breadcrumbs={[
        { to: `/${routes.pinned.root}`, title: t('Pinned Documents') },
      ]}/>
    }
    mainBody={mainBody}
    context={context}
    setActiveId={setActiveId}
  />;

  const newLayout = <MasterLayout>
    <CollectionsAppLayout
      title={t('Saved')}
      tabs={<CollectionsTabs
        activeTab={routes.pinned.root}
      />}
      body={mainBody}
      context={context}
      backgroundClick={() => { setActiveId(''); }}
    />
  </MasterLayout>;

  return showNewNavigation ? newLayout : oldLayout;
};

export default PinnedItemListPage;
